import { useCallback, useRef } from "react";
import Deactivate from "components/blocks/modal/deactivate";
import useStatusMutation from "hook/useStatusMutation";
import { deleteAccount } from "api/accountAPI";
import useApiError from "hook/useApiError";
import { useLocation } from "react-router-dom";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { useQueryClient } from "react-query";
import { useAppDispatch } from "redux/hooks";
import { updateDeletedOrganization } from "redux/stomp/stompSlice";

export default function AccountDelete({
  onModal,
  accountId,
  accountName,
  isMyAccount,
}: {
  onModal?: (type?: string) => void;
  accountId: string;
  accountName: string;
  isMyAccount: boolean;
}) {
  const isDeletingRef = useRef<boolean>(false);
  const { handleErrorNotify, notifySuccess, notifyError, lang } = useApiError();
  const location = useLocation();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const mutationDeleteAccount = useStatusMutation(deleteAccount, {
    onSuccess: (res: any) => {
      if (res.error !== 0) {
        notifyError(lang.validateMsg.account.delete.fail);
      } else {
        notifySuccess(lang.validateMsg.account.delete.success);
        onModal?.("edit");
        if (location.pathname === "/account") {
          queryClient.invalidateQueries(["account"]);
        }
        if (isMyAccount) {
          dispatch(updateDeletedOrganization(true));
        }
      }
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.validateMsg.account.delete.fail);
    },
    onSettled: () => {
      isDeletingRef.current = false;
    },
  });
  const onClickDeactivate = useCallback(() => {
    if (isDeletingRef.current || !mutationDeleteAccount.idle) return;
    isDeletingRef.current = true;
    if (!accountId) return;
    mutationDeleteAccount.mutate(accountId);
  }, [accountId, mutationDeleteAccount]);
  return (
    <Deactivate
      onDeactive={onClickDeactivate}
      title={
        isMyAccount
          ? "Are you sure you want to remove your organization?"
          : "Are you sure you want to remove this organization?"
      }
      subTitle={accountName}
      disabled={isDeletingRef.current || !mutationDeleteAccount.idle}
    />
  );
}
